import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import api from '../api';
import {
  RefreshCw, Printer, Scale, Boxes, Gift, ShieldCheck, Trophy, Download, CalendarRange, CalendarDays,
  ChevronRight, ChevronDown,
} from 'lucide-react';
import CageBalancingTabs from '../components/CageBalancingTabs';
import CageDatePicker from '../components/CageDatePicker';
import { useCageDate } from '../contexts/CageDateContext';
import { currentBusinessDate } from '../utils/casinoDay';

// ----------------------------------------------------------------
// Cage Reports — read-only cage log viewer (per-day) + date-range rollup
// ----------------------------------------------------------------
// Day mode: pick a business date, view that day's Journal/Drop/Jackpot/Free
// Play/Chip Liability logs read-only, each with a CSV export.
// Range mode: pick a start/end date; the page walks the range (client-side,
// capped + pooled) and rolls up per-day totals into a table + bar chart + CSV,
// plus a per-table drop breakdown grouped by game.

const centsToNum = (c) => Number(c || 0) / 100;
const fmtCents = (c) =>
  centsToNum(c).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtHrs = (h) => Number(h || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const denomLabel = (dc) => `$${(Number(dc || 0) / 100).toLocaleString('en-US')}`;
const RANGE_CAP_DAYS = 92;

function pad(n) { return String(n).padStart(2, '0'); }
function addDaysStr(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}
function daysBetween(a, b) {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((new Date(by, bm - 1, bd) - new Date(ay, am - 1, ad)) / 86400000);
}

// Build a CSV string from a 2-D array and trigger a download.
function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

// Run async tasks with a bounded concurrency (avoid hammering the API).
async function pool(items, limit, worker, onProgress) {
  const results = new Array(items.length);
  let i = 0;
  let done = 0;
  async function run() {
    while (i < items.length) {
      const idx = i++;
      results[idx] = await worker(items[idx], idx);
      done += 1;
      if (onProgress) onProgress(done, items.length);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return results;
}

function CsvBtn({ onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 rounded border border-gray-600 px-2.5 py-1 text-xs text-gray-300 hover:bg-gray-700 disabled:opacity-40 print:hidden"
    >
      <Download size={13} /> CSV
    </button>
  );
}

function Section({ title, icon: Icon, right, children }) {
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 mb-4 print:break-inside-avoid">
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-3">
        <h2 className="flex items-center gap-2 text-base font-semibold text-white">
          {Icon ? <Icon size={17} className="text-amber-400" /> : null}
          {title}
        </h2>
        {right || null}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-sm text-gray-500">{msg}</div>;
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-gray-900/60 rounded-lg border border-gray-700 px-3 py-2">
      <div className="text-xs text-gray-400">{label}</div>
      <div className={`text-lg font-semibold font-mono ${accent ? 'text-amber-300' : 'text-white'}`}>{value}</div>
    </div>
  );
}

export default function CageReports() {
  const [mode, setMode] = useState('day');

  // ---- Day mode state ----
  const { businessDate, setBusinessDate } = useCageDate();
  const [loading, setLoading] = useState(false);
  const [journal, setJournal] = useState(null);
  const [journalErr, setJournalErr] = useState(null);
  const [drop, setDrop] = useState(null);
  const [meters, setMeters] = useState(null);
  const [freePlay, setFreePlay] = useState(null);
  const [freePlayErr, setFreePlayErr] = useState(null);
  const [liability, setLiability] = useState(null);
  const [liabilityErr, setLiabilityErr] = useState(null);

  const loadDay = useCallback(async () => {
    if (!businessDate) return;
    setLoading(true);
    const params = { date: businessDate };
    const [je, dp, jp, fp, li] = await Promise.allSettled([
      api.get(`/admin/cbs/je/${businessDate}/preview`),
      api.get('/admin/cbs/drop-counts/summary', { params }),
      api.get('/admin/cbs/jackpots', { params }),
      api.get('/admin/cbs/free-play', { params }),
      api.get('/admin/cbs/liability', { params }),
    ]);
    setJournal(je.status === 'fulfilled' ? je.value.data.journal : null);
    setJournalErr(je.status === 'rejected' ? (je.reason?.response?.data?.error || 'Could not build the journal') : null);
    setDrop(dp.status === 'fulfilled' ? dp.value.data : null);
    setMeters(jp.status === 'fulfilled' ? (jp.value.data.meters || []) : null);
    setFreePlay(fp.status === 'fulfilled' ? fp.value.data : null);
    setFreePlayErr(fp.status === 'rejected' ? (fp.reason?.response?.status === 403 ? 'You do not have permission to view Free Play.' : 'No Free Play data.') : null);
    setLiability(li.status === 'fulfilled' ? li.value.data : null);
    setLiabilityErr(li.status === 'rejected' ? (li.reason?.response?.status === 403 ? 'You do not have permission to view Chip Liability.' : 'No Chip Liability data.') : null);
    setLoading(false);
  }, [businessDate]);

  useEffect(() => { if (mode === 'day') loadDay(); }, [mode, loadDay]);

  const dropRows = drop && drop.perTable
    ? Object.entries(drop.perTable)
        .map(([tableNumber, v]) => ({ tableNumber: Number(tableNumber), ...v }))
        .sort((a, b) => a.tableNumber - b.tableNumber)
    : [];

  // ---- Range mode state ----
  const [rangeStart, setRangeStart] = useState(addDaysStr(currentBusinessDate(), -13));
  const [rangeEnd, setRangeEnd] = useState(currentBusinessDate());
  const [rangeRows, setRangeRows] = useState(null);
  const [rangeBusy, setRangeBusy] = useState(false);
  const [rangeProgress, setRangeProgress] = useState({ done: 0, total: 0 });
  const [rangeError, setRangeError] = useState('');
  const [expandedTable, setExpandedTable] = useState(null);

  const runRange = async () => {
    setRangeError('');
    if (!rangeStart || !rangeEnd) return;
    const span = daysBetween(rangeStart, rangeEnd);
    if (span < 0) { setRangeError('Start date must be on or before the end date.'); return; }
    if (span + 1 > RANGE_CAP_DAYS) { setRangeError(`Range is too large (${span + 1} days). Max ${RANGE_CAP_DAYS} days.`); return; }
    const dates = Array.from({ length: span + 1 }, (_, i) => addDaysStr(rangeStart, i));
    setRangeBusy(true);
    setRangeProgress({ done: 0, total: dates.length });
    setRangeRows(null);
    setExpandedTable(null);

    const rows = await pool(
      dates,
      6,
      async (date) => {
        const params = { date };
        const [dp, jp, fp, li, sh] = await Promise.allSettled([
          api.get('/admin/cbs/drop-counts/summary', { params }),
          api.get('/admin/cbs/jackpots', { params }),
          api.get('/admin/cbs/free-play', { params }),
          api.get('/admin/cbs/liability', { params }),
          api.get('/admin/cbs/shifts', { params }),
        ]);
        const d = dp.status === 'fulfilled' ? dp.value.data : {};
        const meterRows = jp.status === 'fulfilled' ? (jp.value.data.meters || []) : [];
        const f = fp.status === 'fulfilled' ? fp.value.data : {};
        const l = li.status === 'fulfilled' ? li.value.data : {};
        const shifts = sh.status === 'fulfilled' ? (sh.value.data.shifts || []) : [];
        const overShortCents = shifts.reduce((a, s) => a + Number(s.overShortCents || 0), 0);
        const jackpotMeter = meterRows.reduce((a, m) => a + Number(m.meterCents || 0), 0);
        return {
          date,
          // The summary already carries the per-table breakdown; the rollup used
          // to keep only the grand totals and throw this away. Kept now so the
          // per-table range section costs no extra requests.
          perTable: d.perTable || {},
          dropCents: Number(d.grandDrop || 0),
          jackpotDropCents: Number(d.grandJackpot || 0),
          jackpotMeterCents: jackpotMeter,
          fpHeldCents: Number(f.fpTotalCents || 0),
          fpLiabilityCents: Number(f.fpLiabilityCents || 0),
          totalLiabilityCents: l.totalLiabilityCents == null ? null : Number(l.totalLiabilityCents),
          overageCents: l.overageCents == null ? null : Number(l.overageCents),
          overShortCents,
          hasData: !!(d.grandDrop || d.grandJackpot || meterRows.length || f.hasCount),
        };
      },
      (done, total) => setRangeProgress({ done, total })
    );

    setRangeRows(rows);
    setRangeBusy(false);
  };

  const rangeTotals = rangeRows
    ? rangeRows.reduce(
        (a, r) => ({
          days: a.days + (r.hasData ? 1 : 0),
          drop: a.drop + r.dropCents,
          jackpotDrop: a.jackpotDrop + r.jackpotDropCents,
          overShort: a.overShort + r.overShortCents,
        }),
        { days: 0, drop: 0, jackpotDrop: 0, overShort: 0 }
      )
    : null;

  const chartData = rangeRows
    ? rangeRows.map((r) => ({ date: r.date.slice(5), Drop: centsToNum(r.dropCents), Jackpot: centsToNum(r.jackpotDropCents) }))
    : [];

  // ---- CSV exporters (day mode) ----
  const csvJournal = () => {
    if (!journal) return;
    const rows = [['Account', 'Debit', 'Credit'], ...(journal.lines || []).map((l) => [l.account, l.debitCents ? centsToNum(l.debitCents).toFixed(2) : '', l.creditCents ? centsToNum(l.creditCents).toFixed(2) : ''])];
    rows.push(['Totals', centsToNum(journal.totalDebitCents).toFixed(2), centsToNum(journal.totalCreditCents).toFixed(2)]);
    downloadCsv(`cage-journal-${businessDate}.csv`, rows);
  };
  const csvDrop = () => {
    const rows = [['Table', 'Game', 'Drop', 'Jackpot'], ...dropRows.map((r) => [`Table #${r.tableNumber}`, r.game, centsToNum(r.dropCents).toFixed(2), centsToNum(r.jackpotCents).toFixed(2)])];
    rows.push(['Totals', '', centsToNum(drop?.grandDrop).toFixed(2), centsToNum(drop?.grandJackpot).toFixed(2)]);
    downloadCsv(`cage-drop-${businessDate}.csv`, rows);
  };
  const csvJackpot = () => {
    const rows = [['Game', 'Table hrs', 'Meter', 'Admin fee', 'Reserve', 'Prizes', 'Running total'], ...(meters || []).map((m) => [m.game, fmtHrs(m.tableHours), centsToNum(m.meterCents).toFixed(2), centsToNum(m.adminFeeCents).toFixed(2), centsToNum(m.reserveCents).toFixed(2), centsToNum(m.prizesAwardedCents).toFixed(2), centsToNum(m.runningTotalCents).toFixed(2)])];
    downloadCsv(`cage-jackpots-${businessDate}.csv`, rows);
  };
  const csvFreePlay = () => {
    if (!freePlay) return;
    const rows = [['Denom', 'Racks', 'Units', 'Total'], ...(freePlay.denoms || []).map((d) => [denomLabel(d.denomCents), d.racks, d.units, centsToNum(d.totalCents).toFixed(2)])];
    rows.push(['Free Play held', '', '', centsToNum(freePlay.fpTotalCents).toFixed(2)]);
    rows.push(['FP float', '', '', centsToNum(freePlay.fpFloatCents).toFixed(2)]);
    rows.push(['FP liability', '', '', centsToNum(freePlay.fpLiabilityCents).toFixed(2)]);
    downloadCsv(`cage-freeplay-${businessDate}.csv`, rows);
  };
  const csvLiability = () => {
    if (!liability) return;
    const l = liability;
    const rows = [
      ['Field', 'Amount'],
      ['Live chips held', l.liveHeldCents == null ? '' : centsToNum(l.liveHeldCents).toFixed(2)],
      ['Live float', centsToNum(l.liveFloatCents).toFixed(2)],
      ['Live liability', l.liveLiabilityCents == null ? '' : centsToNum(l.liveLiabilityCents).toFixed(2)],
      ['Free Play held', centsToNum(l.fpHeldCents).toFixed(2)],
      ['FP float', centsToNum(l.fpFloatCents).toFixed(2)],
      ['FP liability', centsToNum(l.fpLiabilityCents).toFixed(2)],
      ['Total liability', l.totalLiabilityCents == null ? '' : centsToNum(l.totalLiabilityCents).toFixed(2)],
      ['6668 bank balance', l.bankAccountBalanceCents == null ? '' : centsToNum(l.bankAccountBalanceCents).toFixed(2)],
      ['1417 bank balance', l.bankAccount1417Cents == null ? '' : centsToNum(l.bankAccount1417Cents).toFixed(2)],
      ['Overage / (short)', l.overageCents == null ? '' : centsToNum(l.overageCents).toFixed(2)],
    ];
    downloadCsv(`cage-liability-${businessDate}.csv`, rows);
  };
  // ---- per-table drop across the range -------------------------------
  // Built entirely from the perTable blocks already returned by
  // /admin/cbs/drop-counts/summary for each day in the range, so this costs no
  // extra requests. Grouped by game, because "how did Blackjack do" is the
  // question that usually comes before "how did Table #4 do".
  //
  // The game label comes from the summary rows themselves (which resolve it
  // through cbs_table_routing), never from a hardcoded map here - a table
  // reassignment has to flow through without a frontend change.
  //
  // A table that appears on ANY day in the range is kept for the whole range,
  // even on days it did not drop, so "this table never opened in September"
  // stays visible rather than silently vanishing.
  const perTableRange = rangeRows
    ? (() => {
        const tables = new Map(); // tableNumber -> { table, game, drop, jackpot, days, byDate }
        for (const r of rangeRows) {
          for (const [tableNumber, t] of Object.entries(r.perTable || {})) {
            const key = String(tableNumber);
            if (!tables.has(key)) {
              tables.set(key, {
                table: key, game: t.game || 'UNKNOWN',
                drop: 0, jackpot: 0, days: 0, byDate: [],
              });
            }
            const e = tables.get(key);
            if (t.game && t.game !== 'UNKNOWN') e.game = t.game;
            const drop = Number(t.dropCents || 0);
            const jackpot = Number(t.jackpotCents || 0);
            e.drop += drop;
            e.jackpot += jackpot;
            // "Active" means the table actually dropped, not merely that a row
            // exists - a committed $0.00 count is not a day of play.
            if (drop || jackpot) { e.days += 1; e.byDate.push({ date: r.date, drop, jackpot }); }
          }
        }
        const list = [...tables.values()].sort(
          (a, b) => (a.game === b.game ? Number(a.table) - Number(b.table) : a.game.localeCompare(b.game))
        );
        const games = new Map();
        for (const t of list) {
          if (!games.has(t.game)) games.set(t.game, { game: t.game, drop: 0, jackpot: 0, tables: [] });
          const g = games.get(t.game);
          g.drop += t.drop; g.jackpot += t.jackpot; g.tables.push(t);
        }
        const grand = list.reduce(
          (a, t) => ({ drop: a.drop + t.drop, jackpot: a.jackpot + t.jackpot }),
          { drop: 0, jackpot: 0 }
        );
        return { games: [...games.values()], tables: list, grand };
      })()
    : null;

  const csvPerTable = () => {
    if (!perTableRange) return;
    const rows = [['Game', 'Table', 'Days active', 'Drop', 'Jackpot', 'Total', 'Avg drop per active day']];
    for (const g of perTableRange.games) {
      rows.push([g.game, 'ALL', '', centsToNum(g.drop).toFixed(2), centsToNum(g.jackpot).toFixed(2), centsToNum(g.drop + g.jackpot).toFixed(2), '']);
      for (const t of g.tables) {
        rows.push([
          g.game, `Table #${t.table}`, String(t.days),
          centsToNum(t.drop).toFixed(2), centsToNum(t.jackpot).toFixed(2),
          centsToNum(t.drop + t.jackpot).toFixed(2),
          t.days ? centsToNum(Math.round(t.drop / t.days)).toFixed(2) : '',
        ]);
      }
    }
    rows.push(['TOTAL', '', '', centsToNum(perTableRange.grand.drop).toFixed(2),
      centsToNum(perTableRange.grand.jackpot).toFixed(2),
      centsToNum(perTableRange.grand.drop + perTableRange.grand.jackpot).toFixed(2), '']);
    downloadCsv(`cage-per-table-${rangeStart}_to_${rangeEnd}.csv`, rows);
  };

  const csvRange = () => {
    if (!rangeRows) return;
    const rows = [
      ['Date', 'Drop', 'Jackpot drop', 'Jackpot meter', 'FP held', 'Total liability', 'Overage', 'Over/Short'],
      ...rangeRows.map((r) => [
        r.date,
        centsToNum(r.dropCents).toFixed(2),
        centsToNum(r.jackpotDropCents).toFixed(2),
        centsToNum(r.jackpotMeterCents).toFixed(2),
        centsToNum(r.fpHeldCents).toFixed(2),
        r.totalLiabilityCents == null ? '' : centsToNum(r.totalLiabilityCents).toFixed(2),
        r.overageCents == null ? '' : centsToNum(r.overageCents).toFixed(2),
        centsToNum(r.overShortCents).toFixed(2),
      ]),
    ];
    downloadCsv(`cage-range-${rangeStart}_to_${rangeEnd}.csv`, rows);
  };

  return (
    <div className="max-w-[1100px] mx-auto">
      <CageBalancingTabs />

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <Scale className="text-amber-400" /> Cage Reports
          </h1>
          <p className="text-sm text-gray-400">
            {mode === 'day'
              ? "Read-only view of a single business day's cage logs."
              : 'Roll up per-day cage totals across a date range.'}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-700 overflow-hidden print:hidden">
          <button
            onClick={() => setMode('day')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm ${mode === 'day' ? 'bg-amber-500 text-black font-medium' : 'text-gray-300 hover:bg-gray-700'}`}
          >
            <CalendarDays size={15} /> Day
          </button>
          <button
            onClick={() => setMode('range')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm ${mode === 'range' ? 'bg-amber-500 text-black font-medium' : 'text-gray-300 hover:bg-gray-700'}`}
          >
            <CalendarRange size={15} /> Range
          </button>
        </div>
      </div>

      {mode === 'day' ? (
        <>
          <div className="flex items-end gap-2 mb-4">
            <CageDatePicker />
            <button onClick={loadDay} disabled={loading} className="flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-50">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button onClick={() => window.print()} className="flex items-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-600 px-3 py-2 text-sm font-medium text-black print:hidden">
              <Printer size={15} /> Print
            </button>
          </div>

          <Section
            title="Journal Entry (Balance Sheet)"
            icon={Scale}
            right={
              <div className="flex items-center gap-3">
                {journal ? (
                  <span className={`text-sm font-medium ${journal.balanced ? 'text-emerald-400' : 'text-red-400'}`}>
                    {journal.balanced ? 'Balanced' : `Out of balance ${fmtCents(journal.varianceCents)}`}
                  </span>
                ) : null}
                {journal ? <CsvBtn onClick={csvJournal} /> : null}
              </div>
            }
          >
            {journalErr ? <Empty msg={journalErr} /> : !journal ? <Empty msg="No journal for this date." /> : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-700">
                        <th className="py-2 pr-4 font-medium">Account</th>
                        <th className="py-2 px-4 font-medium text-right">Debit</th>
                        <th className="py-2 pl-4 font-medium text-right">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(journal.lines || []).map((l, i) => {
                        const zero = !l.debitCents && !l.creditCents;
                        return (
                          <tr key={i} className="border-b border-gray-800/60">
                            <td className={`py-1.5 pr-4 ${zero ? 'text-gray-600' : 'text-gray-200'}`}>{l.account}</td>
                            <td className="py-1.5 px-4 text-right font-mono text-gray-200">{l.debitCents ? fmtCents(l.debitCents) : ''}</td>
                            <td className="py-1.5 pl-4 text-right font-mono text-gray-200">{l.creditCents ? fmtCents(l.creditCents) : ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-600 font-semibold text-white">
                        <td className="py-2 pr-4">Totals</td>
                        <td className="py-2 px-4 text-right font-mono">{fmtCents(journal.totalDebitCents)}</td>
                        <td className="py-2 pl-4 text-right font-mono">{fmtCents(journal.totalCreditCents)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {journal.residualCents ? (
                  <p className={`mt-2 text-xs ${Math.abs(journal.residualCents) < 100 ? 'text-gray-500' : 'text-amber-400'}`}>
                    {fmtCents(Math.abs(journal.residualCents))}{' '}
                    {Math.abs(journal.residualCents) < 100 ? 'rounding' : 'variance — review'} booked to Daily Over/Short.
                  </p>
                ) : null}
              </>
            )}
          </Section>

          <Section title="Drop Count" icon={Boxes} right={dropRows.length ? <CsvBtn onClick={csvDrop} /> : null}>
            {!drop || dropRows.length === 0 ? <Empty msg="No drop count recorded for this date." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="py-2 pr-4 font-medium">Table</th>
                      <th className="py-2 px-4 font-medium">Game</th>
                      <th className="py-2 px-4 font-medium text-right">Drop</th>
                      <th className="py-2 pl-4 font-medium text-right">Jackpot</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dropRows.map((r) => (
                      <tr key={r.tableNumber} className="border-b border-gray-800/60">
                        <td className="py-1.5 pr-4 text-gray-200">Table #{r.tableNumber}</td>
                        <td className="py-1.5 px-4 text-gray-400">{r.game}</td>
                        <td className="py-1.5 px-4 text-right font-mono text-gray-200">{fmtCents(r.dropCents)}</td>
                        <td className="py-1.5 pl-4 text-right font-mono text-gray-300">{fmtCents(r.jackpotCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-600 font-semibold text-white">
                      <td className="py-2 pr-4" colSpan={2}>Totals</td>
                      <td className="py-2 px-4 text-right font-mono">{fmtCents(drop.grandDrop)}</td>
                      <td className="py-2 pl-4 text-right font-mono">{fmtCents(drop.grandJackpot)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Section>

          <Section title="Jackpot Log" icon={Trophy} right={meters && meters.length ? <CsvBtn onClick={csvJackpot} /> : null}>
            {!meters || meters.length === 0 ? <Empty msg="No jackpot meters posted for this date." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="py-2 pr-4 font-medium">Game</th>
                      <th className="py-2 px-4 font-medium text-right">Table hrs</th>
                      <th className="py-2 px-4 font-medium text-right">Meter</th>
                      <th className="py-2 px-4 font-medium text-right">Admin fee</th>
                      <th className="py-2 px-4 font-medium text-right">Reserve</th>
                      <th className="py-2 px-4 font-medium text-right">Prizes</th>
                      <th className="py-2 pl-4 font-medium text-right">Running total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meters.map((m) => (
                      <tr key={m.id} className="border-b border-gray-800/60">
                        <td className="py-1.5 pr-4 text-gray-200 font-medium">{m.game}</td>
                        <td className="py-1.5 px-4 text-right font-mono text-gray-300">{fmtHrs(m.tableHours)}</td>
                        <td className="py-1.5 px-4 text-right font-mono text-gray-200">{fmtCents(m.meterCents)}</td>
                        <td className="py-1.5 px-4 text-right font-mono text-gray-300">{fmtCents(m.adminFeeCents)}</td>
                        <td className="py-1.5 px-4 text-right font-mono text-gray-300">{fmtCents(m.reserveCents)}</td>
                        <td className="py-1.5 px-4 text-right font-mono text-gray-300">{fmtCents(m.prizesAwardedCents)}</td>
                        <td className="py-1.5 pl-4 text-right font-mono text-amber-300">{fmtCents(m.runningTotalCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section
            title="Free Play Log"
            icon={Gift}
            right={
              <div className="flex items-center gap-3">
                {freePlay && freePlay.locked ? <span className="text-xs text-emerald-400">Signed &amp; locked</span> : null}
                {freePlay && freePlay.hasCount ? <CsvBtn onClick={csvFreePlay} /> : null}
              </div>
            }
          >
            {freePlayErr ? <Empty msg={freePlayErr} /> : !freePlay || !freePlay.hasCount ? <Empty msg="No Free Play count recorded for this date." /> : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-700">
                        <th className="py-2 pr-4 font-medium">Denom</th>
                        <th className="py-2 px-4 font-medium text-right">Racks</th>
                        <th className="py-2 px-4 font-medium text-right">Units</th>
                        <th className="py-2 pl-4 font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(freePlay.denoms || []).map((d) => (
                        <tr key={d.denomCents} className="border-b border-gray-800/60">
                          <td className="py-1.5 pr-4 text-gray-200">{denomLabel(d.denomCents)}</td>
                          <td className="py-1.5 px-4 text-right font-mono text-gray-300">{d.racks}</td>
                          <td className="py-1.5 px-4 text-right font-mono text-gray-300">{d.units}</td>
                          <td className="py-1.5 pl-4 text-right font-mono text-gray-200">{fmtCents(d.totalCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Stat label="Free Play held" value={fmtCents(freePlay.fpTotalCents)} />
                  <Stat label="FP float" value={fmtCents(freePlay.fpFloatCents)} />
                  <Stat label="FP liability" value={fmtCents(freePlay.fpLiabilityCents)} accent />
                </div>
              </>
            )}
          </Section>

          <Section
            title="Chip Liability Log"
            icon={ShieldCheck}
            right={
              <div className="flex items-center gap-3">
                {liability && liability.signed ? <span className="text-xs text-emerald-400">Signed</span> : null}
                {liability ? <CsvBtn onClick={csvLiability} /> : null}
              </div>
            }
          >
            {liabilityErr ? <Empty msg={liabilityErr} /> : !liability ? <Empty msg="No Chip Liability data for this date." /> : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Stat label="Live chips held" value={liability.liveHeldCents == null ? '—' : fmtCents(liability.liveHeldCents)} />
                  <Stat label="Live float" value={fmtCents(liability.liveFloatCents)} />
                  <Stat label="Live liability" value={liability.liveLiabilityCents == null ? '—' : fmtCents(liability.liveLiabilityCents)} />
                  <Stat label="Free Play held" value={fmtCents(liability.fpHeldCents)} />
                  <Stat label="FP float" value={fmtCents(liability.fpFloatCents)} />
                  <Stat label="FP liability" value={fmtCents(liability.fpLiabilityCents)} />
                  <Stat label="Total liability" value={liability.totalLiabilityCents == null ? '—' : fmtCents(liability.totalLiabilityCents)} accent />
                  <Stat label="6668 bank balance" value={liability.bankAccountBalanceCents == null ? '—' : fmtCents(liability.bankAccountBalanceCents)} />
                  <Stat label="1417 bank balance" value={liability.bankAccount1417Cents == null ? '—' : fmtCents(liability.bankAccount1417Cents)} />
                  <Stat label="Overage / (short)" value={liability.overageCents == null ? '—' : fmtCents(liability.overageCents)} accent />
                </div>
                {liability.notes ? <p className="mt-3 text-xs text-gray-400"><span className="text-gray-500">Notes: </span>{liability.notes}</p> : null}
                {liability.signed && liability.signedAt ? <p className="mt-1 text-xs text-gray-500">Signed {new Date(liability.signedAt).toLocaleString()}.</p> : null}
              </>
            )}
          </Section>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Start date</label>
              <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">End date</label>
              <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <button onClick={runRange} disabled={rangeBusy} className="flex items-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-600 px-3 py-2 text-sm font-medium text-black disabled:opacity-50">
              <RefreshCw size={15} className={rangeBusy ? 'animate-spin' : ''} /> {rangeBusy ? 'Running…' : 'Run rollup'}
            </button>
            {rangeRows && !rangeBusy ? <CsvBtn onClick={csvRange} /> : null}
          </div>

          {rangeError ? <div className="mb-4 rounded-lg border border-red-700/50 bg-red-900/20 p-3 text-sm text-red-300">{rangeError}</div> : null}

          {rangeBusy ? (
            <div className="mb-4">
              <div className="text-xs text-gray-400 mb-1">Loading {rangeProgress.done} / {rangeProgress.total} days…</div>
              <div className="h-2 bg-gray-800 rounded overflow-hidden">
                <div className="h-full bg-amber-500 transition-all" style={{ width: `${rangeProgress.total ? (rangeProgress.done / rangeProgress.total) * 100 : 0}%` }} />
              </div>
            </div>
          ) : null}

          {rangeRows && rangeTotals ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Stat label="Days with data" value={`${rangeTotals.days} / ${rangeRows.length}`} />
                <Stat label="Total drop" value={fmtCents(rangeTotals.drop)} accent />
                <Stat label="Total jackpot drop" value={fmtCents(rangeTotals.jackpotDrop)} />
                <Stat label="Total over / short" value={fmtCents(rangeTotals.overShort)} accent />
              </div>

              <Section title="Daily drop & jackpot" icon={Boxes}>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: '#fff' }}
                        formatter={(v) => `$${Number(v).toLocaleString('en-US')}`}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Drop" fill="#f0bb53" />
                      <Bar dataKey="Jackpot" fill="#6b7280" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Section>

              <Section title="Per-day rollup" icon={CalendarRange} right={<CsvBtn onClick={csvRange} />}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-700">
                        <th className="py-2 pr-4 font-medium">Date</th>
                        <th className="py-2 px-4 font-medium text-right">Drop</th>
                        <th className="py-2 px-4 font-medium text-right">Jackpot</th>
                        <th className="py-2 px-4 font-medium text-right">FP held</th>
                        <th className="py-2 px-4 font-medium text-right">Total liability</th>
                        <th className="py-2 pl-4 font-medium text-right">Over / short</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rangeRows.map((r) => (
                        <tr key={r.date} className={`border-b border-gray-800/60 ${r.hasData ? '' : 'opacity-40'}`}>
                          <td className="py-1.5 pr-4 text-gray-200">{r.date}</td>
                          <td className="py-1.5 px-4 text-right font-mono text-gray-200">{fmtCents(r.dropCents)}</td>
                          <td className="py-1.5 px-4 text-right font-mono text-gray-300">{fmtCents(r.jackpotDropCents)}</td>
                          <td className="py-1.5 px-4 text-right font-mono text-gray-300">{fmtCents(r.fpHeldCents)}</td>
                          <td className="py-1.5 px-4 text-right font-mono text-gray-300">{r.totalLiabilityCents == null ? '—' : fmtCents(r.totalLiabilityCents)}</td>
                          <td className="py-1.5 pl-4 text-right font-mono text-gray-200">{fmtCents(r.overShortCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-600 font-semibold text-white">
                        <td className="py-2 pr-4">Totals</td>
                        <td className="py-2 px-4 text-right font-mono">{fmtCents(rangeTotals.drop)}</td>
                        <td className="py-2 px-4 text-right font-mono">{fmtCents(rangeTotals.jackpotDrop)}</td>
                        <td className="py-2 px-4 text-right font-mono text-gray-500">—</td>
                        <td className="py-2 px-4 text-right font-mono text-gray-500">—</td>
                        <td className="py-2 pl-4 text-right font-mono">{fmtCents(rangeTotals.overShort)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Section>

              {perTableRange && perTableRange.tables.length > 0 && (
                <Section
                  title="Per-table drop"
                  icon={Boxes}
                  right={<CsvBtn onClick={csvPerTable} />}
                >
                  <div className="mb-3 text-xs text-gray-500">
                    Summed across {rangeRows.length} day{rangeRows.length === 1 ? '' : 's'}.
                    Days counts the days a table actually dropped, so Avg/day compares tables
                    that ran different numbers of days. Click a table for its daily figures.
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-700">
                          <th className="py-2 pr-4 font-medium">Game / table</th>
                          <th className="py-2 px-4 font-medium text-right">Days</th>
                          <th className="py-2 px-4 font-medium text-right">Drop</th>
                          <th className="py-2 px-4 font-medium text-right">Jackpot</th>
                          <th className="py-2 px-4 font-medium text-right">Total</th>
                          <th className="py-2 pl-4 font-medium text-right">Avg drop/day</th>
                        </tr>
                      </thead>
                      <tbody>
                        {perTableRange.games.map((g) => (
                          <Fragment key={g.game}>
                            <tr className="border-b border-gray-700/60 bg-gray-800/40">
                              <td className="py-1.5 pr-4 font-semibold text-amber-300">{g.game}</td>
                              <td className="py-1.5 px-4" />
                              <td className="py-1.5 px-4 text-right font-mono font-semibold text-gray-100">{fmtCents(g.drop)}</td>
                              <td className="py-1.5 px-4 text-right font-mono font-semibold text-gray-300">{fmtCents(g.jackpot)}</td>
                              <td className="py-1.5 px-4 text-right font-mono font-semibold text-gray-100">{fmtCents(g.drop + g.jackpot)}</td>
                              <td className="py-1.5 pl-4" />
                            </tr>
                            {g.tables.map((t) => {
                              const open = expandedTable === t.table;
                              const dead = t.days === 0;
                              return (
                                <Fragment key={t.table}>
                                  <tr
                                    onClick={() => !dead && setExpandedTable(open ? null : t.table)}
                                    className={`border-b border-gray-800/60 ${dead ? 'opacity-40' : 'cursor-pointer hover:bg-gray-800/40'}`}
                                  >
                                    <td className="py-1.5 pr-4 pl-4 text-gray-200">
                                      <span className="inline-flex items-center gap-1">
                                        {dead ? (
                                          <span className="w-[14px]" />
                                        ) : open ? (
                                          <ChevronDown size={14} className="text-gray-500" />
                                        ) : (
                                          <ChevronRight size={14} className="text-gray-500" />
                                        )}
                                        Table #{t.table}
                                      </span>
                                    </td>
                                    <td className="py-1.5 px-4 text-right font-mono text-gray-400">{t.days}</td>
                                    <td className="py-1.5 px-4 text-right font-mono text-gray-200">{fmtCents(t.drop)}</td>
                                    <td className="py-1.5 px-4 text-right font-mono text-gray-300">{fmtCents(t.jackpot)}</td>
                                    <td className="py-1.5 px-4 text-right font-mono text-gray-200">{fmtCents(t.drop + t.jackpot)}</td>
                                    <td className="py-1.5 pl-4 text-right font-mono text-gray-400">
                                      {t.days ? fmtCents(Math.round(t.drop / t.days)) : '—'}
                                    </td>
                                  </tr>
                                  {open &&
                                    t.byDate.map((d) => (
                                      <tr key={`${t.table}-${d.date}`} className="border-b border-gray-800/40 bg-gray-900/40 text-xs">
                                        <td className="py-1 pr-4 pl-10 text-gray-500">{d.date}</td>
                                        <td className="py-1 px-4" />
                                        <td className="py-1 px-4 text-right font-mono text-gray-400">{fmtCents(d.drop)}</td>
                                        <td className="py-1 px-4 text-right font-mono text-gray-500">{fmtCents(d.jackpot)}</td>
                                        <td className="py-1 px-4 text-right font-mono text-gray-400">{fmtCents(d.drop + d.jackpot)}</td>
                                        <td className="py-1 pl-4" />
                                      </tr>
                                    ))}
                                </Fragment>
                              );
                            })}
                          </Fragment>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-600 font-semibold text-white">
                          <td className="py-2 pr-4">Totals</td>
                          <td className="py-2 px-4" />
                          <td className="py-2 px-4 text-right font-mono">{fmtCents(perTableRange.grand.drop)}</td>
                          <td className="py-2 px-4 text-right font-mono">{fmtCents(perTableRange.grand.jackpot)}</td>
                          <td className="py-2 px-4 text-right font-mono">{fmtCents(perTableRange.grand.drop + perTableRange.grand.jackpot)}</td>
                          <td className="py-2 pl-4" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {perTableRange.grand.drop !== rangeTotals.drop && (
                    <div className="mt-3 rounded border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200/80">
                      Per-table drop ({fmtCents(perTableRange.grand.drop)}) does not equal the
                      per-day rollup ({fmtCents(rangeTotals.drop)}). They come from the same
                      source, so a gap here means a day returned a grand total without matching
                      table rows — worth a look before trusting either figure.
                    </div>
                  )}
                </Section>
              )}
            </>
          ) : !rangeBusy ? (
            <Empty msg="Pick a start and end date, then Run rollup." />
          ) : null}
        </>
      )}
    </div>
  );
}
